import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Input,
  Select,
  Spin,
} from 'antd';
import moment from 'moment';
import {
  ListChecks,
  Plus,
} from '@phosphor-icons/react';
import { captureAnalytics } from '../../../actions/preload';
import DatePicker from '../../../components/DatePicker';
import Typography from '../../../components/Typography';
import Button from '../../../components/Button';
import {
  createCallFollowUp,
  fetchCallFollowUp,
  fetchCallFollowUpOptions,
} from '../CallFollowUps/callFollowUpApi';
import InboundExistingFollowUpEditor from '../CallFollowUps/InboundExistingFollowUpEditor';
import {
  NOTE_MAX_LENGTH,
  blankFollowUpForm,
  createDefaultsFromOptions,
  assigneeOptionLabel,
  normalizeId,
  safeFollowUpErrorMessage,
  selectOptions,
} from '../CallFollowUps/callFollowUpFormUtils';

const useCallFollowUpPanel = ({
  callLog,
  onFollowUpLinked,
}) => {
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createForm, setCreateForm] = useState(blankFollowUpForm);
  const [createdFollowup, setCreatedFollowup] = useState(null);
  const [followup, setFollowup] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const callLogRef = useRef(callLog);

  callLogRef.current = callLog;
  const followUpId = callLog?.call_followup_id || createdFollowup?.id || null;

  const assigneeOptions = useMemo(
    () => selectOptions(options?.assignees, assigneeOptionLabel),
    [options],
  );
  const statusOptions = useMemo(() => selectOptions(options?.call_followup_statuses), [options]);
  const dispositionOptions = useMemo(() => selectOptions(options?.call_dispositions), [options]);

  const currentRequestContext = () => ({
    callId: callLog?.id || null,
    databaseCallLogId: callLog?.database_call_log_id || null,
  });

  const requestStillCurrent = (requestContext) => {
    const currentCallLog = callLogRef.current || {};
    if ((currentCallLog.id || null) !== requestContext.callId) return false;
    if ((currentCallLog.database_call_log_id || null) !== requestContext.databaseCallLogId) return false;
    return true;
  };

  useEffect(() => {
    setOptions(null);
    setCreateError(null);
    setCreateForm(blankFollowUpForm);

    if (!callLog?.database_call_log_id) {
      setOptionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setOptionsLoading(true);
    fetchCallFollowUpOptions({ database_call_log_id: callLog.database_call_log_id })
      .then((result) => {
        if (cancelled) return;
        setOptionsLoading(false);
        setOptions(result);
        const firstStatus = result.call_followup_statuses?.[0];
        setCreateForm((form) => ({
          ...form,
          statusId: form.statusId || firstStatus?.id || null,
        }));
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setCreateError(safeFollowUpErrorMessage(fetchError, 'Unable to load follow-up options.', {
            notFoundMessage: 'Unable to load follow-up options. Close and reopen the call to refresh.',
          }));
          setOptionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [callLog?.database_call_log_id]);

  useEffect(() => {
    setCreateForm(createDefaultsFromOptions(options));
    setCreateSaving(false);
  }, [callLog?.id, options]);

  useEffect(() => {
    setCreateError(null);
    setCreatedFollowup(null);
  }, [callLog?.id, callLog?.database_call_log_id]);

  useEffect(() => {
    setDetailError(null);

    if (!followUpId) {
      setFollowup(null);
      setDetailLoading(false);
      return undefined;
    }

    if (createdFollowup && normalizeId(createdFollowup.id) === normalizeId(followUpId)) {
      setFollowup(createdFollowup);
      setDetailLoading(false);
      return undefined;
    }

    if (normalizeId(followup?.id) === normalizeId(followUpId)) {
      setDetailLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFollowup(null);
    setDetailLoading(true);
    fetchCallFollowUp(followUpId)
      .then((result) => {
        if (!cancelled) setFollowup(result);
      })
      .catch((fetchError) => {
        if (!cancelled) setDetailError(safeFollowUpErrorMessage(fetchError, 'Unable to load this follow-up.'));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [followUpId, callLog?.id, createdFollowup?.id]);

  const handleCreate = async () => {
    setCreateError(null);
    if (!callLog?.database_call_log_id) return;
    if (!createForm.dueDate || !createForm.callDispositionId || !createForm.statusId) {
      setCreateError('Due date, final outcome, and initial status are required.');
      return;
    }

    setCreateSaving(true);
    const requestContext = currentRequestContext();
    try {
      const created = await createCallFollowUp({
        database_call_log_id: callLog.database_call_log_id,
        assignee_id: createForm.assigneeId || null,
        due_date: createForm.dueDate,
        call_disposition_id: createForm.callDispositionId,
        initial_entry: {
          call_followup_status_id: createForm.statusId,
          note: createForm.note,
        },
      });
      if (!requestStillCurrent(requestContext)) return;
      if (!createForm.assigneeId) {
        captureAnalytics('call_followup_create_unassigned', { surface: 'voice_inbound_panel' });
      }
      setCreatedFollowup(created);
      setFollowup(created);
      onFollowUpLinked(created.id, callLog);
    } catch (saveError) {
      if (requestStillCurrent(requestContext)) {
        setCreateError(safeFollowUpErrorMessage(saveError, 'Unable to create follow-up. Please try again.'));
      }
    } finally {
      if (requestStillCurrent(requestContext)) setCreateSaving(false);
    }
  };

  const handleFollowUpChanged = (updatedFollowup) => {
    setFollowup(updatedFollowup);
    if (normalizeId(createdFollowup?.id) === normalizeId(updatedFollowup.id)) {
      setCreatedFollowup(updatedFollowup);
    }
  };

  return {
    assigneeOptions,
    createForm,
    detailLoading,
    dispositionOptions,
    error: createError || detailError,
    followup,
    handleCreate,
    handleFollowUpChanged,
    optionsLoading,
    saving: createSaving,
    setCreateForm,
    options,
    statusOptions,
  };
};

const CallFollowUpPanel = ({
  callLog,
  onFollowUpLinked,
}) => {
  const {
    assigneeOptions,
    createForm,
    detailLoading,
    dispositionOptions,
    error,
    followup,
    handleCreate,
    handleFollowUpChanged,
    options,
    optionsLoading,
    saving,
    setCreateForm,
    statusOptions,
  } = useCallFollowUpPanel({ callLog, onFollowUpLinked });

  const renderFormFields = (form, setForm, includeStatus = false) => (
    <div className="call-followup-form-grid">
      <div className="call-followup-input">
        <span>Assignee</span>
        <Select
          value={form.assigneeId}
          onChange={(value) => setForm({ ...form, assigneeId: value || null })}
          options={assigneeOptions}
          placeholder="Select assignee"
          allowClear
        />
      </div>
      <div className="call-followup-input">
        <span>Due date</span>
        <DatePicker
          value={form.dueDate ? moment(form.dueDate, 'YYYY-MM-DD') : null}
          onChange={(_, dateString) => setForm({ ...form, dueDate: dateString || null })}
        />
      </div>
      <div className="call-followup-input">
        <span>Final outcome</span>
        <Select
          value={form.callDispositionId}
          onChange={(value) => setForm({ ...form, callDispositionId: value === undefined ? null : value })}
          options={dispositionOptions}
          placeholder="Select outcome"
          allowClear={!includeStatus}
        />
      </div>
      {includeStatus && (
        <div className="call-followup-input">
          <span>Initial status</span>
          <Select
            value={form.statusId}
            onChange={(value) => setForm({ ...form, statusId: value })}
            options={statusOptions}
            placeholder="Select status"
          />
        </div>
      )}
    </div>
  );

  const renderCreateState = () => (
    <div className="call-followup-create" data-testid="call-followup-create">
      {renderFormFields(createForm, setCreateForm, true)}
      <div className="call-followup-input call-followup-note">
        <span>Initial note</span>
        <Input.TextArea
          aria-label="Initial note"
          value={createForm.note}
          onChange={(event) => setCreateForm({ ...createForm, note: event.target.value })}
          placeholder="Optional note"
          maxLength={NOTE_MAX_LENGTH}
        />
      </div>
      <div className="call-followup-actions">
        <Button
          type="primary"
          size="small"
          label="Create follow-up"
          leftIcon={<Plus size={16} />}
          onClick={handleCreate}
          disabled={saving || optionsLoading}
        />
      </div>
    </div>
  );

  if (!callLog?.database_call_log_id) {
    return (
      <section className="call-followup-panel" data-testid="call-followup-unavailable">
        <Header />
        <Alert message="Follow-up unavailable for this call." type="info" showIcon />
      </section>
    );
  }

  return (
    <section className="call-followup-panel">
      <Header />
      {error && <Alert message={error} type="error" showIcon />}
      {(optionsLoading || detailLoading) && <Spin size="small" />}
      {!optionsLoading && !detailLoading && (followup ? (
        <InboundExistingFollowUpEditor
          followup={followup}
          options={options}
          optionsLoading={optionsLoading}
          onFollowUpChanged={handleFollowUpChanged}
        />
      ) : renderCreateState())}
    </section>
  );
};

const Header = () => (
  <div className="voice-call-context-section-header call-followup-header">
    <ListChecks size={24} weight="fill" />
    <Typography variant="text" size="small" className="voice-call-context-section-title" bold>
      Follow Up
    </Typography>
  </div>
);

CallFollowUpPanel.propTypes = {
  callLog: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    database_call_log_id: PropTypes.number,
    call_followup_id: PropTypes.number,
  }),
  onFollowUpLinked: PropTypes.func,
};

CallFollowUpPanel.defaultProps = {
  callLog: null,
  onFollowUpLinked: () => {},
};

export default CallFollowUpPanel;
